import React, { useState, useMemo, useRef, useEffect } from "react";
import { View, Text, StyleSheet, TouchableOpacity, FlatList, TextInput } from "react-native";
import { Ionicons, FontAwesome6 } from "@expo/vector-icons";
import metadata from "@expo/vector-icons/build/vendor/react-native-vector-icons/glyphmaps/FontAwesome6Free_meta.json";

const IconPickerView = ({ onSelectIcon, selectedIcon }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [activeFilter, setActiveFilter] = useState("all");
	const tooltipRef = useRef(null);

	useEffect(() => {
		// Create tooltip element
		const tooltip = document.createElement("div");
		tooltip.style.cssText = `
			position: fixed;
			background-color: #2A2A2A;
			color: #fff;
			padding: 6px 10px;
			border-radius: 4px;
			font-size: 13px;
			font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
			pointer-events: none;
			z-index: 1000;
			display: none;
			box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
			transform: translateY(2px);
			letter-spacing: 0.2px;
			font-weight: 500;
			white-space: nowrap;
			border: 1px solid rgba(255, 255, 255, 0.1);
			backdrop-filter: blur(8px);
		`;
		document.body.appendChild(tooltip);
		tooltipRef.current = tooltip;

		// Cleanup
		return () => {
			document.body.removeChild(tooltip);
		};
	}, []);

	const handleMouseMove = (e, iconName) => {
		const tooltip = tooltipRef.current;
		if (!tooltip) return;

		tooltip.textContent = iconName;
		tooltip.style.display = "block";
		tooltip.style.left = `${e.clientX + 10}px`;
		tooltip.style.top = `${e.clientY + 10}px`;
	};

	const handleMouseLeave = () => {
		if (tooltipRef.current) {
			tooltipRef.current.style.display = "none";
		}
	};

	const filteredIcons = useMemo(() => {
		let icons = activeFilter === "all" ? Object.values(metadata).flat() : metadata[activeFilter] || [];

		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase().trim();
			icons = icons.filter((iconName) => iconName.toLowerCase().includes(query));
		}

		return icons;
	}, [activeFilter, searchQuery]);

	const renderIcon = ({ item: iconName, index }) => (
		<TouchableOpacity
			key={iconName + index}
			style={[styles.iconItem, selectedIcon === iconName && styles.iconItemSelected]}
			onPress={() => {
				onSelectIcon && onSelectIcon(iconName);
				handleMouseLeave();
			}}
			onMouseMove={(e) => handleMouseMove(e, iconName)}
			onMouseLeave={handleMouseLeave}>
			<FontAwesome6 name={iconName} size={24} color="#fff" />
		</TouchableOpacity>
	);

	return (
		<View style={styles.container}>
			<View style={styles.searchContainer}>
				<Ionicons name="search" size={20} color="#666" style={styles.searchIcon} />
				<TextInput style={styles.searchInput} placeholder="Search icons..." placeholderTextColor="#666" value={searchQuery} onChangeText={setSearchQuery} />
			</View>

			<View style={styles.filterTabs}>
				<TouchableOpacity style={[styles.filterTab, activeFilter === "all" && styles.activeFilterTab]} onPress={() => setActiveFilter("all")}>
					<Text style={[styles.filterTabText, activeFilter === "all" && styles.activeFilterTabText]}>All</Text>
				</TouchableOpacity>
				{getSegments().map((segment) => (
					<TouchableOpacity key={segment} style={[styles.filterTab, activeFilter === segment && styles.activeFilterTab]} onPress={() => setActiveFilter(segment)}>
						<Text style={[styles.filterTabText, activeFilter === segment && styles.activeFilterTabText]}>{segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase()}</Text>
					</TouchableOpacity>
				))}
			</View>

			<FlatList data={filteredIcons} renderItem={renderIcon} keyExtractor={(item, index) => item + index} numColumns={18} style={styles.iconGrid} contentContainerStyle={styles.iconGridContent} columnWrapperStyle={styles.row} />
		</View>
	);
};

const getSegments = () => {
	return Object.keys(metadata);
};

// Helper function to get icon names for demo
const getIconName = (index) => {
	const icons = ["home", "person", "settings", "heart", "star", "notifications", "mail", "search", "camera", "bookmark", "add-circle", "trash", "cloud", "download", "share", "location", "time", "calendar", "chatbubble", "cart"];
	return icons[index % icons.length];
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		display: "flex",
		flexDirection: "column",
		padding: 16,
	},
	searchContainer: {
		flexDirection: "row",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		paddingHorizontal: 12,
		marginBottom: 16,
	},
	searchIcon: {
		marginRight: 8,
	},
	searchInput: {
		flex: 1,
		height: 40,
		color: "#fff",
		fontSize: 14,
	},
	filterTabs: {
		flexDirection: "row",
		marginBottom: 16,
		gap: 8,
	},
	filterTab: {
		paddingVertical: 6,
		paddingHorizontal: 12,
		borderRadius: 4,
		backgroundColor: "#2A2A2A",
		borderWidth: 1,
		borderColor: "transparent",
	},
	activeFilterTab: {
		backgroundColor: "rgba(56, 139, 255, 0.15)",
		borderColor: "#388BFF",
	},
	filterTabText: {
		color: "#fff",
		fontSize: 12,
	},
	activeFilterTabText: {
		color: "#388BFF",
		fontWeight: "500",
	},
	iconGrid: {
		flex: 1,
	},
	iconGridContent: {
		paddingBottom: 20,
	},
	row: {
		flex: 1,
		gap: 6,
		marginBottom: 6,
	},
	iconItem: {
		width: 48,
		height: 48,
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: "#2A2A2A",
		borderRadius: 4,
		borderWidth: 1,
		borderColor: "transparent",
	},
	iconItemSelected: {
		backgroundColor: "#1A1A1A",
		borderColor: "#007AFF",
	},
	tooltip: {
		backgroundColor: "#333",
		padding: 8,
		borderRadius: 4,
		zIndex: 1000,
		pointerEvents: "none", // ensures the tooltip doesn't interfere with mouse events
		boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
	},
	tooltipText: {
		color: "#fff",
		fontSize: 12,
	},
});

export default IconPickerView;
